import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    format,
    addDays,
    addMonths,
    addYears,
    startOfMonth,
    endOfMonth,
    isWithinInterval,
    differenceInDays,
} from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography,
    CircularProgress, 
    Tooltip, 
    Divider,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,
    Table, 
    TableHead, 
    TableBody, 
    TableRow, 
    TableCell,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT, DIVERT_RETURN_STATUS } from 'state/constants';
import { 
    parseUTCDate, 
    capitalizeFirstLetter,
    pluralize,
    filterFlights,
} from 'state/helpers';
import { fetchFlights } from 'state/api';
import { compareFlights } from './utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import StyledButton from 'components/StyledButton';
import { boxStyles as styles } from 'components/CustomStyles';
import FilterButton from 'components/FilterButton';
import NumberControl from 'components/NumberControl';
import FlightFilterDialog from 'components/FlightFilterDialog';
import ChartDialog from 'components/chartNivo/ChartDialog';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';


const formatPeriodLabel = (period) => {
    if (period.includes(' - ')) {
        return period.split(' - ')
            .map(p => { const [yy, mm, dd] = p.split('.'); return `${dd}.${mm}.${yy}`; })
            .join(' - ');
    }
    // "yyyy.MM" → "MM.yy"
    const parts = period.split('.');
    if (parts.length === 2) return `${parts[1]}.${parts[0].slice(2)}`;
    return period;
};

const TABLE_ROW_DEFS = [
    { key: 'flights',       viewKey: 'flights',          label: 'Flights',              unit: '',           decimals: 0 },
    { key: 'dailyFlights',  viewKey: 'dailyFlights',     label: 'Daily Flights',        unit: '',           decimals: 2 },
    { key: 'totalBH',       viewKey: 'totalBlockHours',  label: 'Total Block Hours',    unit: '',           decimals: 1 },
    { key: 'avgBM',         viewKey: 'blockMinutes',     label: 'Block Time Avg',       unit: 'min',        decimals: 0 },
    { key: 'airMinutes',    viewKey: 'airMinutes',       label: 'Air Time Avg',         unit: 'min',        decimals: 0 },
    { key: 'btDiff',        viewKey: 'btDiff',           label: 'BT Diff Avg',          unit: 'min',        decimals: 1 },
    { key: 'depOTP',        viewKey: 'depOtp',           label: 'Dep OTP',              unit: '%',          decimals: 1 },
    { key: 'arrOTP',        viewKey: 'arrOtp',           label: 'Arr OTP',              unit: '%',          decimals: 1 },
    { key: 'depDelay',      viewKey: 'depDelayMinutes',  label: 'Dep Delay Avg',        unit: 'min',        decimals: 0 },
    { key: 'arrDelay',      viewKey: 'arrDelayMinutes',  label: 'Arr Delay Avg',        unit: 'min',        decimals: 0 },
    { key: 'pax',           viewKey: 'pax',              label: 'Pax',                  unit: '',           decimals: 0 },
    { key: 'loadFactor',    viewKey: 'loadFactor',       label: 'Load Factor',          unit: '%',          decimals: 1 },
    { key: 'tat',           viewKey: 'tat',              label: 'TAT Avg',              unit: 'min',        decimals: 0 },
    { key: 'tatDiff',       viewKey: 'tatDiff',          label: 'TAT Diff Avg',         unit: 'min',        decimals: 1 },
    { key: 'taxiOut',       viewKey: 'taxiOut',          label: 'Taxi Out Avg',         unit: 'min',        decimals: 0 },
    { key: 'taxiIn',        viewKey: 'taxiIn',           label: 'Taxi In Avg',          unit: 'min',        decimals: 0 },
];

const viewOptions = [
    { value: 'flights', label: 'Flights' },
    { value: 'dailyFlights', label: 'Daily Flights' },
    { value: 'totalBlockHours', label: 'Total Block Hrs' },
    { value: 'blockMinutes', label: 'Block Time' },
    { value: 'btDiff', label: 'BT Diff' },
    { value: 'airMinutes', label: 'Air Time' },
    { value: 'depOtp', label: 'Dep OTP' },
    { value: 'arrOtp', label: 'Arr OTP' },
    { value: 'depDelayMinutes', label: 'Dep Delays' },
    { value: 'arrDelayMinutes', label: 'Arr Delays' },
    { value: 'loadFactor', label: 'Load Factor' },
    { value: 'pax', label: 'Pax' },
    { value: 'tat', label: 'TAT' },
    { value: 'tatDiff', label: 'TAT Diff' },
    { value: 'taxiOut', label: 'Taxi Out' },
    { value: 'taxiIn', label: 'Taxi In' },
];


const FlightCompareChart = () => {
    useAuthCheck();    
    const timeMode = useSelector((state) => state.timemode);
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState(null);
    const [dialog, setDialog] = useState(null); 

    const [yearOffset, setYearOffset] = useState(-1);
    const [offset, setOffset] = useState(-1);
    const [view, setView] = useState('flights');
    const [compareBy, setCompareBy] = useState('week');
    const [flightObjects, setFlightObjects] = useState({});

    const absOffset = Math.abs(offset);
    const direction = offset === 0 ? "current" : offset < 0 ? 'previous' : 'next';  
    const absYearOffset = Math.abs(yearOffset);
    const offSetDirection = yearOffset === 0 ? "current" : yearOffset < 0 ? 'previous' : 'next';  

    const ranges = {
        week: 103,
        month: 23,
        year: 5,
    };
    const range = ranges[compareBy] ?? 23;
    const min = -range;
    const max = range;

    const FETCH_TYPE = params?.find((item) => item.param === "FETCH_TYPE")?.value || "BATCH"; // ['BATCH', 'SEQUENCE']
    
    const compareOptions = [
        { value: "week", label: "Week" },
        { value: "month", label: "Month" },
        { value: "year", label: "Year" },
        { value: "weekinyears", label: `Week in ${Math.abs(yearOffset) + 1} ${pluralize('Year', Math.abs(yearOffset) + 1)}` },
    ];
      
    const getCompareLabel = (value) => compareOptions.find(opt => opt.value === value)?.label ?? '';
    const label = getCompareLabel(compareBy);
    const title = `${capitalizeFirstLetter(direction)} ${pluralize(label, offset)?.toLowerCase()}`;
    
    const [data, setData] = useState({
        flights: [],        
        totalBlockHours: [],        
        blockTime: [],        
        btDiff: [],        
        airTime: [],        
        depOtp: [],        
        arrOtp: [],        
        loadFactor: [],        
        tat: [],        
        tatDiff: [],        
        taxiOut: [],        
        taxiIn: [],        
    }); 

    const normalizeFlights = (flights, fromDate, toDate, timeMode) =>
        flights.filter(flight => {
            const flightDate = timeMode === 'UTC'
                ? flight.flightDate
                : flight.flightDateLocalBase;

            return isWithinInterval(flightDate, {
                start: fromDate,
                end: toDate,
            });
        })
        .filter(f => 
            !f.flightStatus.toLowerCase().includes('cancelled') &&
            !f.flightStatus.toLowerCase().includes('deleted') &&
            !DIVERT_RETURN_STATUS.includes(f.flightStatus)
    );

    const getRangeTemplates = (compareBy, yearOffset = -1) => {
        switch (compareBy) {
            case 'month':
                return [
                    { type: 'month' }
                ];
            case 'week':
                return [
                    { type: 'week' }
                ];
            case 'year':
                return [
                    { type: 'year' }
                ];
            case 'weekinyears':
                return Array.from({ length: Math.abs(yearOffset) + 1 },
                    (_, i) => ({
                        type: 'week',
                        yearOffset: yearOffset < 0 ? -i : i
                    })
                );
            default:
                return [];
        }
    };

    const handleFlightsCompare = useCallback(async () => {
        if (!startDate) return;

        try {
            setError(null);
            setLoading(true);

            const flightsCollection = {};
            const tasks = [];

            const begin = offset <= 0 ? offset : 0;
            const end = offset > 0 ? offset : 0;

            const ranges = getRangeTemplates(compareBy);

            for (const range of ranges) {
                for (let i = begin; i <= end; i++) {
                    let from, to, key;

                    if (range.type === 'month') {
                        from = startOfMonth(addMonths(startDate, i));
                        to = endOfMonth(from);
                        key = format(from, 'yyyy.MM');
                    }

                    if (range.type === 'week') {
                        from = addDays(startDate, i * 7);
                        from = range.yearOffset
                            ? addYears(from, range.yearOffset)
                            : from;

                        to = addDays(from, 6);
                        key = `${format(from, 'yy.MM.dd')} - ${format(to, 'yy.MM.dd')}`;
                    }

                    if (range.type === 'year') {
                        from = addYears(startDate, i);
                        to = addYears(endDate, i);
                        key = `${format(from, 'yy.MM.dd')} - ${format(to, 'yy.MM.dd')}`;
                    }

                    const fromDate = parseUTCDate(from);
                    const toDate = parseUTCDate(to);

                    if (FETCH_TYPE === 'SEQUENCE') {
                        const flights = await fetchFlights(
                            addDays(fromDate, -1),
                            addDays(toDate, 1)
                        );

                        if (!flights) continue;

                        flightsCollection[key] = normalizeFlights(
                            flights,
                            fromDate,
                            toDate,
                            timeMode
                        );
                    }

                    if (FETCH_TYPE === 'BATCH') {
                        tasks.push(fetchFlights(addDays(fromDate, -1), addDays(toDate, 1))
                            .then(flights => ({
                                flights,
                                fromDate,
                                toDate,
                                key,
                            }))
                        );
                    }
                }
            }

            if (FETCH_TYPE === 'BATCH') {
                const results = await Promise.all(tasks);

                results.forEach(({ flights, fromDate, toDate, key }) => {
                    if (!flights) return;

                    flightsCollection[key] = normalizeFlights(
                        flights,
                        fromDate,
                        toDate,
                        timeMode
                    );
                });
            }

            setFlightObjects(flightsCollection);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [
        timeMode,
        startDate,
        endDate,
        offset,
        compareBy,
        FETCH_TYPE,
    ]);
    
    const handleCompareChange = useCallback(
        (e) => setCompareBy(e.target.value)
    , []);

    useEffect(() => {
        setFlightObjects({});
    }, [compareBy]);

    const filteredFlights = useMemo(() => {
        if (!flightObjects || !filters) return flightObjects || {};

        return Object.fromEntries(
            Object.entries(flightObjects).map(([key, flights]) => {
                const filteredFlights = filterFlights({ flights, filters, timeMode });
                return [key, filteredFlights];
            }).filter(([_, filteredFlights]) => filteredFlights.length > 0)
        );
    }, [flightObjects, filters, timeMode]);

    const dailyFlightsData = useMemo(() => {
        if (!data.flights || data.flights.length === 0) return [];
        const getDays = (periodKey) => {
            if (compareBy === 'week' || compareBy === 'weekinyears') return 7;
            if (compareBy === 'year') return Math.max(1, differenceInDays(endDate, startDate) + 1);
            // month key format: 'yyyy.MM'
            const parts = periodKey.split('.');
            if (parts.length === 2) return new Date(+parts[0], +parts[1], 0).getDate();
            return 30;
        };
        return data.flights.map(series => ({
            ...series,
            id: series.id === 'Flt' ? 'Flt/Day' : series.id === 'FltAvg' ? 'Avg/Day' : series.id,
            data: series.data.map(point => ({
                ...point,
                y: parseFloat((point.y / getDays(point.x)).toFixed(1)),
            })),
        }));
    }, [data.flights, compareBy, startDate, endDate]);

    const {
        acTypeOptions,
        flightRegOptions,
        sectorTypeOptions,
        departureTypeOptions,
        arrivalTypeOptions,
    } = useMemo(() => {
        const allFlights = Object.values(flightObjects || {}).flat();

        const buildOptions = (key) =>
            [...new Set(allFlights.map(f => f[key]).filter(Boolean))].sort();

        return {
            allFlights,
            acTypeOptions: buildOptions('acType'),
            flightRegOptions: buildOptions('flightReg'),
            sectorTypeOptions: buildOptions('sectorType'),
            departureTypeOptions: buildOptions('departureType'),
            arrivalTypeOptions: buildOptions('arrivalType'),
        };
    }, [flightObjects]);
    
    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);
    
    const handleFilterApply = (filterValues) => {
        setFilters(filterValues); 
    };
    
    const handleChartOpen = (chart) => {
        setDialog({
            type: "chart",
            payload: (
                <Box sx={{ ...styles }}>
                    <Box height="100%" width="100%" sx={chart.style}>
                        {chart.component}
                    </Box>
                </Box>
            )
        });
    };
    
    useEffect(() => {
        const configs = {
            flights: [],
            totalBlockHours: ['barline', 'BH', 'BHAvg', 'totalBlockHours'],
            blockMinutes: ['barline', 'BlockTime', 'BlockTimeAvg', 'blockMinutes'],
            btDiff: ['barline', 'BTDiff', 'BTDiffAvg', 'btDiff'],
            airMinutes: ['barline', 'AirTime', 'AirTimeAvg', 'airMinutes'],
            depOtp: ['barline', 'OTP', 'OTPAvg', 'depOtp'],
            arrOtp: ['barline', 'OTP', 'OTPAvg', 'arrOtp'],
            depDelayMinutes: ['barline', 'Delay', 'DelayAvg', 'depDelayMinutes'],
            arrDelayMinutes: ['barline', 'Delay', 'DelayAvg', 'arrDelayMinutes'],
            loadFactor: ['barline', 'LoadFactor', 'LoadFactorAvg', 'loadFactor'],
            pax: ['barline', 'Pax', 'PaxAvg', 'pax'],
            tat: ['barline', 'TAT', 'TATAvg', 'tat'],
            tatDiff: ['barline', 'TATDiff', 'TATDiffAvg', 'tatDiff'],
            taxiOut: ['barline', 'TaxiOut', 'TaxiOutAvg', 'taxiOut'],
            taxiIn: ['barline', 'TaxiIn', 'TaxiInAvg', 'taxiIn'],
        };

        const result = Object.fromEntries(
            Object.entries(configs)?.map(([key, args]) => {
                const value = args.length === 0
                    ? compareFlights(filteredFlights)
                    : compareFlights(filteredFlights, ...args);
                return [key, value];
            })
        );

        setData(result);
    }, [filteredFlights]); 
    
    
    const defaultTitle = (() => {
        switch (compareBy) {
            case 'week': {
                return `Compare ${format(startDate, 'ddMMM')} 
                    - ${format(addDays(startDate, 6), 'ddMMM')} 
                    to ${absOffset} ${direction} ${pluralize('week', absOffset)}`;
            }

            case 'weekinyears': {
                return `Compare week ${format(startDate, 'ddMMM')} - 
                    ${format(addDays(startDate, 6),'ddMMM')} 
                    to ${absOffset} ${direction} ${pluralize('week', absOffset)} 
                    of ${absYearOffset} ${offSetDirection} ${pluralize('year', absYearOffset)}`;
            }

            case 'month': {
                return `Compare ${format(startDate, 'MMMyyyy')} 
                to ${absOffset} ${direction} ${pluralize('month', absOffset)}`;
            }

            case 'year': {
                return `Compare ${format(startDate, 'ddMMMyy')} 
                    - ${format(endDate, 'ddMMMyy')} 
                    to ${absOffset} ${direction} ${pluralize('year', absOffset)}`;
            }

            default:
                return `Compare ${format(startDate, 'ddMMMyy')} 
                    - ${format(endDate, 'ddMMMyy')}`;
        }
    })();

    const tableData = useMemo(() => {
        const periods = data.flights?.[0]?.data?.map(d => d.x) ?? [];
        if (!periods.length) return { periods: [], rows: [] };

        const dataMap = {
            flights:     data.flights,
            totalBH:     data.totalBlockHours,
            avgBM:       data.blockMinutes,
            airMinutes:  data.airMinutes,
            btDiff:      data.btDiff,
            depOTP:      data.depOtp,
            arrOTP:      data.arrOtp,
            depDelay:    data.depDelayMinutes,
            arrDelay:    data.arrDelayMinutes,
            pax:         data.pax,
            loadFactor:  data.loadFactor,
            tat:         data.tat,
            tatDiff:     data.tatDiff,
            taxiOut:     data.taxiOut,
            taxiIn:      data.taxiIn,
            dailyFlights: dailyFlightsData,
        };

        const rows = TABLE_ROW_DEFS.map(({ key, viewKey, label, unit, decimals }) => ({
            key,
            viewKey,
            label,
            unit,
            decimals,
            values: periods.map((_, i) => {
                const val = dataMap[key]?.[0]?.data?.[i]?.y;
                return val != null ? val : null;
            }),
        }));

        return { periods, rows };
    }, [data, dailyFlightsData]);

    const bottomOffSet = compareBy ==='month' ? 0 : 55;
    const chartMap = {
        flights: [
            { component: <SortBarMultiLinesChart data={data.flights} title={defaultTitle} bottomLegend={label} leftLegend='Flights' digit={0} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={130} lineTranslateXOffSet={0} />, style: {} },
        ],
        dailyFlights: [
            { component: <SortBarMultiLinesChart data={dailyFlightsData} title={defaultTitle} bottomLegend={label} leftLegend='Flt/Day' digit={1} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={130} lineTranslateXOffSet={0} />, style: {} },
        ],
        totalBlockHours: [
            { component: <SortBarMultiLinesChart data={data.totalBlockHours} title={defaultTitle} bottomLegend={label} leftLegend='Block Hours' digit={0} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={130} lineTranslateXOffSet={0} />, style: {} },
        ],
        blockMinutes: [
            { component: <SortBarMultiLinesChart data={data.blockMinutes} title={defaultTitle} bottomLegend={label} leftLegend='Minutes' digit={0} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={66} lineTranslateXOffSet={-35} />, style: {} },
        ],
        btDiff: [
            { component: <SortBarMultiLinesChart data={data.btDiff} title={defaultTitle} bottomLegend={label} leftLegend='Minutes' digit={1} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={105} lineTranslateXOffSet={-15} />, style: {} },
        ],
        airMinutes: [
            { component: <SortBarMultiLinesChart data={data.airMinutes} title={defaultTitle} bottomLegend={label} leftLegend='Minutes' digit={0} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={90} lineTranslateXOffSet={-25} />, style: {} },
        ],
        depOtp: [
            { component: <SortBarMultiLinesChart data={data.depOtp} title={defaultTitle} bottomLegend={label} leftLegend='OTP (%)' digit={1} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={120} lineTranslateXOffSet={-10} />, style: {} },
        ],
        arrOtp: [
            { component: <SortBarMultiLinesChart data={data.arrOtp} title={defaultTitle} bottomLegend={label} leftLegend='OTP (%)' digit={1} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={120} lineTranslateXOffSet={-10} />, style: {} },
        ],
        depDelayMinutes: [
            { component: <SortBarMultiLinesChart data={data.depDelayMinutes} title={defaultTitle} bottomLegend={label} leftLegend='Minutes' digit={0} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={100} lineTranslateXOffSet={-15} />, style: {} },
        ],
        arrDelayMinutes: [
            { component: <SortBarMultiLinesChart data={data.arrDelayMinutes} title={defaultTitle} bottomLegend={label} leftLegend='Minutes' digit={0} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={100} lineTranslateXOffSet={-15} />, style: {} },
        ],
        loadFactor: [
            { component: <SortBarMultiLinesChart data={data.loadFactor} title={defaultTitle} bottomLegend={label} leftLegend='Load Factor (%)' digit={1} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={60} lineTranslateXOffSet={-40} />, style: {} },
        ],
        pax: [
            { component: <SortBarMultiLinesChart data={data.pax} title={defaultTitle} bottomLegend={label} leftLegend='No. of Pax' digit={1} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={120} lineTranslateXOffSet={-5} />, style: {} },
        ],
        tat: [
            { component: <SortBarMultiLinesChart data={data.tat} title={defaultTitle} bottomLegend={label} leftLegend='Minutes' digit={0} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={115} lineTranslateXOffSet={-5} />, style: {} },
        ],
        tatDiff: [
            { component: <SortBarMultiLinesChart data={data.tatDiff} title={defaultTitle} bottomLegend={label} leftLegend='Minutes' digit={0} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={100} lineTranslateXOffSet={-20} />, style: {} },
        ],
        taxiOut: [
            { component: <SortBarMultiLinesChart data={data.taxiOut} title={defaultTitle} bottomLegend={label} leftLegend='Minutes' digit={0} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={100} lineTranslateXOffSet={-20} />, style: {} },
        ],
        taxiIn: [
            { component: <SortBarMultiLinesChart data={data.taxiIn} title={defaultTitle} bottomLegend={label} leftLegend='Minutes' digit={0} bottomOffSet={bottomOffSet} bottomLegendOffSet={bottomOffSet} barTranslateXOffSet={105} lineTranslateXOffSet={-15} />, style: {} },
        ],
    };
        
    const renderCharts = (charts, customStyles) => (
        <Box
            display="flex"
            flexDirection="column"
            gap={2}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}                     
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles, ...chart.style }}
                >
                    {chart.component}
                </Box>                
            ))}
        </Box>
    );

    const renderTable = useMemo(() => {
        const row = tableData.rows.find(r => r.viewKey === view);
        if (!row || !tableData.periods.length) return null;

        return (
            <Box 
                sx={{ 
                    maxHeight: LAYOUT_HEIGHT, 
                    overflowX: 'auto', 
                    overflowY: 'auto', 
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.alt', minWidth: 120, whiteSpace: 'nowrap' }}>
                                Period
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'background.alt', minWidth: 120 }}>
                                {row.label}{row.unit ? ` (${row.unit})` : ''}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableData.periods.map((period, i) => {
                            const { decimals, values } = row;
                            const val = values[i];
                            const display = val != null ? parseFloat(val.toFixed(decimals)) : '—';
                            return (
                                <TableRow key={period} hover sx={{ bgcolor: i % 2 === 0 ? 'background.default' : 'background.alt' }}>
                                    <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                                        {formatPeriodLabel(period)}
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: '0.8rem' }}>
                                        {display}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </Box>
        );
    }, [tableData, view]);

    const dialogContent = useMemo(() => {
        if (!dialog) return null;

        if (dialog.type === "chart") return dialog.payload;
        if (dialog.type === "table") return renderTable;

        return null;
    }, [dialog, renderTable]);


    return (        
        <Box className="main-container-box">
            <FlexBetween>
                <Header 
                    title="Flight Compare" 
                    subtitle={`${loading ? 'Loading...' : 'List of Charts'}`}
                />     
                {!loading && (
                    <Box 
                        display="flex"
                        flexDirection={isMobile ? "column" : "row"}
                        alignItems="flex-end" 
                        gap={isMobile ? 1 : 2}
                        mb={isMobile ? 1 : 0}
                    >
                        <Box 
                            display="flex" 
                            alignItems="center" 
                            gap={1} 
                        >      
                            {isDesktop && compareBy === 'weekinyears' && (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Tooltip title={"No. of years"} arrow>
                                        <span>
                                            <NumberControl
                                                min={-6}
                                                max={6}
                                                value={yearOffset}
                                                onChange={(value) => { setYearOffset(value) }} 
                                                textFieldWidth={45}
                                            />
                                        </span>
                                    </Tooltip>
                                    <Divider
                                        orientation="vertical"
                                        flexItem
                                    />
                                </Box> 
                            )}

                            <Tooltip title={title} arrow>
                                <span>
                                    <NumberControl
                                        min={min}
                                        max={max}
                                        value={offset}
                                        onChange={(value) => { setOffset(value) }} 
                                        textFieldWidth={45}
                                    />
                                </span>
                            </Tooltip>
                            
                            <FormControl 
                                size="small"
                                sx={{ width: { xs: '7rem', sm: '9rem' } }}
                            >
                                <InputLabel id="compare-by-label">Compare by</InputLabel>
                                <Select
                                    labelId="compare-by-label"
                                    label="Compare by"
                                    value={compareBy}
                                    onChange={handleCompareChange}
                                >
                                    {compareOptions?.map(({ value, label }) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            
                            <Tooltip title="Compare" arrow>
                                <span>
                                    <StyledButton onClick={handleFlightsCompare}>
                                        <CachedIcon />
                                    </StyledButton>
                                </span>
                            </Tooltip>
                        </Box>

                        <Box 
                            display="flex" 
                            alignItems="center" 
                            gap={1}
                        >
                            <FormControl
                                size="small"
                                sx={{ width: { xs: '7rem', sm: '9rem' } }}
                            >
                                <InputLabel id="view-by-label">View</InputLabel>
                                <Select
                                    labelId="view-by-label"
                                    label="View"
                                    value={view}
                                    onChange={(e) => setView(e.target.value)}
                                >
                                    {viewOptions.map(({ value, label }) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FilterButton
                                filters={filters}
                                onClick={handleFilterOpen}
                            />
                        </Box>
                    </Box>                    
                )}                    
            </FlexBetween>            
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    ref={scrollRef}
                    height={LAYOUT_HEIGHT}  
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    sx={{  overflowY: 'auto' }}
                >
                    {view in chartMap && renderCharts(chartMap[view], { minHeight: LAYOUT_HEIGHT })}

                    {tableData.periods.length > 0 && (
                        <Box
                            onDoubleClick={() => setDialog({ type: "table" })}
                            sx={{
                                border: '1px solid',
                                borderColor: 'neutral.deep',
                                borderRadius: 1,
                                cursor: 'pointer',
                            }}
                        >
                            {renderTable}
                        </Box>
                    )}

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}

            {isFilterDialogOpen &&
                <FlightFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    acTypeOptions={acTypeOptions} 
                    flightRegOptions={flightRegOptions}
                    sectorTypeOptions={sectorTypeOptions}
                    departureTypeOptions={departureTypeOptions}
                    arrivalTypeOptions={arrivalTypeOptions}
                    currentFilters={filters}
                />
            }

            <ChartDialog
                open={!!dialog}
                onClose={() => setDialog(null)}
                chartComponent={dialogContent}
            />
        </Box>
    );
};

export default FlightCompareChart;
